import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ArrowLeft, Sparkles } from 'lucide-react';

const PaymentReturnHandler = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const submitBooking = async () => {
            const txnId = searchParams.get('txnId');
            const bookingDataStr = localStorage.getItem('bookingData');

            if (!txnId || !bookingDataStr) {
                setError("Missing payment or booking information.");
                setLoading(false);
                return;
            }

            const bookingData = JSON.parse(bookingDataStr);

            // Avoid duplicate submissions
            if (localStorage.getItem('lastSubmittedTxn') === txnId) {
                navigate('/success', { state: { bookingData } });
                return;
            }

            try {
                const response = await axios.post(
                    'https://eventbooking.apanel.leewaysofttech.com/BOOK_YOUR_SHOW_API/book_event.php',
                    {
                        event_id: bookingData.eventId,
                        email: '', // Same as Flutter code
                        phone: bookingData.phone,
                        seats: Array.isArray(bookingData.seats) ? bookingData.seats.join(',') : bookingData.seats,
                        total_amount: bookingData.totalAmount,
                        payment_status: 'SUCCESS',
                        payment_id: txnId,
                    }
                );

                if (response.data.success === 1) {
                    localStorage.setItem('lastSubmittedTxn', txnId);
                    localStorage.setItem('lastBooking', JSON.stringify(bookingData));
                    navigate('/success', { state: { bookingData } });
                } else {
                    setError(response.data.message || "Booking failed. Please contact support.");
                }
            } catch (err) {
                setError("An error occurred while confirming your booking.");
            } finally {
                setLoading(false);
            }
        };

        submitBooking();
    }, [searchParams, navigate]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-cream p-6 text-center font-outfit relative overflow-hidden">
                {/* Background Decor */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

                <div className="bg-white p-12 rounded-[3rem] shadow-2xl shadow-primary/5 border border-primary/10 max-w-md w-full relative z-10">
                    <div className="w-24 h-24 bg-red-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-red-100">
                        <AlertCircle className="text-red-500" size={48} />
                    </div>
                    <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.3em] mb-4 block">Verification Failed</span>
                    <h2 className="text-3xl font-playfair font-black text-secondary mb-4">Payment Check Failed</h2>
                    <p className="text-secondary/60 font-medium mb-10 leading-relaxed text-sm">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full bg-secondary text-white py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-xs hover:bg-primary transition-all shadow-xl shadow-secondary/10"
                    >
                        <ArrowLeft className="inline-block mr-2 w-4 h-4" /> Return to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-cream font-outfit relative overflow-hidden">
             {/* Background Decor */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>

            <div className="flex flex-col items-center relative z-10">
                <div className="relative mb-8">
                    <div className="w-20 h-20 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles size={24} className="text-primary/40" />
                    </div>
                </div>
                <h2 className="text-3xl font-playfair font-black text-secondary mb-4">Confirming Your Journey</h2>
                <p className="text-primary font-black text-[10px] tracking-[0.4em] uppercase opacity-70">Securing your tickets with trust...</p>
            </div>
        </div>
    );
};

export default PaymentReturnHandler;
